import { useContext, useState, useEffect } from 'react'

import { faCrown } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Form,
  ModalContext, SidebarModal,
  SquareIconWithBackground,
} from '@domain-group/suit-theme'

import { launchModal } from '@domain-group/javascript-utils'

import useOrderItem from '@hooks/useOrderItem'
import useRedirectUrlWithCacheKey from '@hooks/useRedirectUrlWithCacheKey'
import useDispatch from '@hooks/useDispatch'

import * as orderItemActions from '@redux/modules/orderItem'

import PageContext from '@contexts/pageContext'

import type { ArtworkModel } from '@models/types'

const MODAL_KEY = 'ArtworkBuilderSelectionModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ArtworkBuilderSelectionModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const { closeModal } = callbacks || {}

  const [existingArtworks, setExistingArtworks] = useState<ArtworkModel[]>([])

  const dispatch = useDispatch()

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const { orderItem: initOrderItem = {} } = modalPayload

  const orderItemPayload = useOrderItem(initOrderItem)
  const {
    // @ts-expect-error TS2339
    agency, orderItem, campaign: { id: campaignId }, callbacks: { createArtwork },
  } = orderItemPayload
  const { redirectUrl } = useRedirectUrlWithCacheKey([orderItem.id])

  const { callbacks: pageCallbacks } = useContext(PageContext)

  // Load existing artworks count
  useEffect(() => {
    const loadArtworksCount = async () => {
      if (!showModal || !orderItem.id || !campaignId || !orderItem.service_product_id) return
      try {
        const { loadCompatibleArtworks: loadFn } = orderItemActions
        const result = await dispatch(loadFn(campaignId, orderItem.service_product_id))
        if (result.success && result.data){
          setExistingArtworks(result.data)
          return
        }

        setExistingArtworks([])
      } catch {
        setExistingArtworks([])
      }
    }

    loadArtworksCount()
  }, [orderItem.id, campaignId])

  const handleSelectExistingArtwork = (artworks: ArtworkModel[]) => {
    if (!artworks.length) return

    launchModal({
      callbacks: pageCallbacks,
      modalKey: 'DuplicateOrderItemArtworkModal',
      payload: {
        existingArtworks: artworks,
        orderItem,
      },
    })

    closeModal()
  }

  const handleCanvaSelection = () => {
    launchModal({
      callbacks: pageCallbacks,
      modalKey: 'CanvaTemplateSelectionModal',
      payload: {
        agency,
        orderItem,
      },
    })

    closeModal()
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={MODAL_KEY}
      showModal={showModal}
    >
      <SidebarModal.Header callbacks={callbacks} title="Select" titleSecondLine="Builder" />

      <SidebarModal.Content>

        <Form>

          {existingArtworks?.length > 0 && (
            <Form.Field label="Use Existing Artwork">
              <SidebarModal.Button
                iconLeft={(
                  <SquareIconWithBackground
                    backgroundColor="#F8F7F4"
                    color="tan"
                    icon={<FontAwesomeIcon icon={faCrown} />}
                  />
                )}
                showAngleIcon
                subtitle={`${existingArtworks.length} available`}
                title="Select an existing artwork"
                onClick={() => handleSelectExistingArtwork(existingArtworks)}
              />
            </Form.Field>
          )}

          <Form.Field label="Use Realhub&apos;s Artwork Builder" marginTop="large">
            <SidebarModal.Button
              iconLeft={(
                <SquareIconWithBackground
                  backgroundColor="#F8F7F4"
                  color="tan"
                  icon={<FontAwesomeIcon icon={faCrown} />}
                />
              )}
              showAngleIcon
              subtitle="Create custom artwork"
              title="Realhub Artwork Builder"
              onClick={() => createArtwork(redirectUrl)}
            />
          </Form.Field>

          <Form.Field label="Use Canva" marginTop="large">
            <SidebarModal.Button
              iconLeft={(
                <SquareIconWithBackground
                  backgroundColor="#F8F7F4"
                  color="tan"
                  icon={<FontAwesomeIcon icon={faCrown} />}
                />
              )}
              showAngleIcon
              subtitle="Design with Templates"
              title="Canva"
              onClick={() => handleCanvaSelection()}
            />
          </Form.Field>
        </Form>
      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ArtworkBuilderSelectionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
